import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import defaultImg from '../../asset/project/default_1.png';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { getPageMessage, getSettingsMessage } from '../../shared/constants/pageMessages';
import { ApprovalRequestData } from '../../shared/dto/EntityRepository';
import {
  AlertPosition,
  AlertType,
  ApprovalRequestStatus,
  ApprovalRequestType,
} from '../../shared/enum/EnumRepository';
import {
  useApprovalRequestActions,
  useApprovalRequestState,
} from '../../shared/hooks/useApi/useApprovalRequest';
import {
  BlackFitBtn,
  PaperBlurFitBtn,
} from '../../shared/component/headless/button/BtnBody';
import { SearchMessage, SearchResults } from '../../shared/component/modal/SearchModalLayout';
import { useAuthStore } from '../../shared/store/authStore';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import {
  getApprovalRequestMessage,
  isIncomingApprovalRequest,
} from '../../shared/util/approvalRequest';

type ApprovalRequestAction = 'accept' | 'reject' | 'cancel' | 'acknowledge';

interface ProcessingAction {
  requestId: string;
  action: ApprovalRequestAction;
}

interface ApprovalRequestSectionProps {
  isMobile?: boolean;
}

interface ApprovalWorkThumbnailProps {
  originUrl: string;
  alt: string;
}

const ApprovalWorkThumbnail: React.FC<ApprovalWorkThumbnailProps> = ({
  originUrl,
  alt,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useOptimizedImageUrl(originUrl, containerRef);
  return (
    <WorkThumbnailFrame ref={containerRef}>
      <WorkThumbnailImage src={imageUrl || defaultImg} alt={alt} />
    </WorkThumbnailFrame>
  );
};

const ApprovalRequestSection: React.FC<ApprovalRequestSectionProps> = ({
  isMobile = false,
}) => {
  const user = useAuthStore((state) => state.user);
  const { enqueueAlert } = useStandardAlertStore();
  const { approvalRequestList, status } = useApprovalRequestState();
  const {
    getApprovalRequestList,
    acceptApprovalRequest,
    rejectApprovalRequest,
    cancelApprovalRequest,
    acknowledgeApprovalRequest,
    discardApprovalRequest,
  } = useApprovalRequestActions();
  const [processingAction, setProcessingAction] = useState<ProcessingAction | null>(null);
  const [failedRequestId, setFailedRequestId] = useState<string | null>(null);
  const canceledRequestIdsRef = useRef(new Set<string>());
  const discardApprovalRequestRef = useRef(discardApprovalRequest);
  discardApprovalRequestRef.current = discardApprovalRequest;

  useEffect(() => () => {
    canceledRequestIdsRef.current.forEach((requestId) => {
      discardApprovalRequestRef.current(requestId);
    });
  }, []);

  if (!user) return null;

  const handleAction = async (
    action: ApprovalRequestAction,
    request: ApprovalRequestData,
  ) => {
    if (processingAction) return;
    setProcessingAction({ requestId: request.id, action });
    setFailedRequestId(null);
    try {
      const data = { id: request.id };
      let updated = false;
      if (action === 'accept') updated = await acceptApprovalRequest(data);
      if (action === 'reject') updated = await rejectApprovalRequest(data);
      if (action === 'cancel') updated = await cancelApprovalRequest(data);
      if (action === 'acknowledge') updated = await acknowledgeApprovalRequest(data);
      if (updated && action === 'cancel') {
        canceledRequestIdsRef.current.add(request.id);
      }
      if (!updated) setFailedRequestId(request.id);
    } catch {
      setFailedRequestId(request.id);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleAccept = (request: ApprovalRequestData) => {
    if (request.type === ApprovalRequestType.TEXT_COPY) {
      if (request.detail.texts.some((text) => !text.available)) return;
      const groupName = request.requester.name.trim()
        || request.requester.handle.trim()
        || request.requester.aui;
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('CONFIRM_ACCEPT_TEXT_COPY', {
          groupName,
          count: request.detail.texts.length,
        }),
        callBack: () => handleAction('accept', request),
      });
      return;
    }

    if (request.type === ApprovalRequestType.WORK_COPY) {
      if (request.detail.works.some((work) => !work.available)) return;
      const groupName = request.requester.name.trim()
        || request.requester.handle.trim()
        || request.requester.aui;
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('CONFIRM_ACCEPT_WORK_COPY', {
          groupName,
          count: request.detail.works.length,
        }),
        callBack: () => handleAction('accept', request),
      });
      return;
    }

    if (request.type !== ApprovalRequestType.GROUP_MEMBER_CONNECTION) {
      void handleAction('accept', request);
      return;
    }

    const groupName = request.requester.name.trim()
      || request.requester.handle.trim()
      || request.requester.aui;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: `${getAlertMessage('CONFIRM_ACCEPT_GROUP_MEMBER_CONNECTION', {
        groupName,
      })}\n\n${getSettingsMessage('GROUP_MEMBER_CONNECTION_REQUEST_EXPLAIN')}`,
      callBack: () => handleAction('accept', request),
    });
  };

  const getActionLabel = (
    requestId: string,
    action: ApprovalRequestAction,
    defaultLabel: 'ACCEPT' | 'REJECT' | 'CANCEL' | 'OK',
  ) => processingAction?.requestId === requestId
    && processingAction.action === action
    ? getSettingsMessage('APPROVAL_REQUEST_PROCESSING')
    : getBtnName(defaultLabel);

  const renderListContent = () => {
    if (status === 'loading' || status === 'idle') {
      return <SearchMessage>{getSettingsMessage('APPROVAL_REQUEST_LOADING')}</SearchMessage>;
    }
    if (status === 'error') {
      return (
        <ErrorMessage role="alert">
          <span>{getSettingsMessage('APPROVAL_REQUEST_FAILED')}</span>
          <RetryButton $isMobile={isMobile} onClick={() => void getApprovalRequestList()}>
            {getBtnName('RETRY')}
          </RetryButton>
        </ErrorMessage>
      );
    }
    if (approvalRequestList.length === 0) {
      return <SearchMessage>{getSettingsMessage('APPROVAL_REQUEST_EMPTY')}</SearchMessage>;
    }

    return approvalRequestList.map((request) => {
      const isIncoming = isIncomingApprovalRequest(request, user.aui);
      const isPending = request.status === ApprovalRequestStatus.PENDING;
      const isCanceled = request.status === ApprovalRequestStatus.CANCELED;
      const isProcessing = processingAction?.requestId === request.id;
      const actionFailed = failedRequestId === request.id;
      const areActionsDisabled = processingAction !== null;
      const hasUnavailableText = request.type === ApprovalRequestType.TEXT_COPY
        && isPending
        && request.detail.texts.some((text) => !text.available);
      const hasUnavailableWork = request.type === ApprovalRequestType.WORK_COPY
        && isPending
        && request.detail.works.some((work) => !work.available);
      const hasUnavailableCopyItem = hasUnavailableText || hasUnavailableWork;

      return (
        <RequestItem key={request.id} $isMobile={isMobile} aria-busy={isProcessing}>
          <RequestContent>
            <RequestMessage>
              {getApprovalRequestMessage(
                isCanceled
                  ? { ...request, status: ApprovalRequestStatus.PENDING }
                  : request,
                user.aui,
              )}
            </RequestMessage>
            {request.type === ApprovalRequestType.TEXT_COPY && (
              <CopyList>
                {request.detail.texts.map((text) => {
                  const unavailable = isPending && !text.available;
                  const title = text.title?.trim()
                    || text.source.title?.trim()
                    || getPageMessage('TEXT_COPY_IMPORT_UNTITLED');
                  const meta = [
                    text.source.author?.trim(),
                    text.source.title?.trim(),
                    text.subtitle?.trim(),
                    text.prodYear,
                  ].filter((value) =>
                    value !== null && value !== undefined && value !== '').join(' · ');
                  return (
                    <CopyItem key={text.sourceTextId} $unavailable={unavailable}>
                      <CopyInfo>
                        <CopyTitle>{title}</CopyTitle>
                        {meta && <CopyMeta>{meta}</CopyMeta>}
                      </CopyInfo>
                      {unavailable && (
                        <UnavailableLabel>
                          {getSettingsMessage('TEXT_COPY_ITEM_UNAVAILABLE')}
                        </UnavailableLabel>
                      )}
                    </CopyItem>
                  );
                })}
              </CopyList>
            )}
            {hasUnavailableText && (
              <RequestWarning role="alert">
                {getSettingsMessage('TEXT_COPY_REQUEST_UNAVAILABLE')}
              </RequestWarning>
            )}
            {request.type === ApprovalRequestType.WORK_COPY && (
              <CopyList>
                {request.detail.works.map((work) => {
                  const unavailable = isPending && !work.available;
                  const title = work.title?.trim()
                    || work.titleEn?.trim()
                    || getPageMessage('WORK_COPY_IMPORT_UNTITLED');
                  const titleEn = work.titleEn?.trim();
                  const meta = [
                    work.artist?.name?.trim(),
                    titleEn && titleEn !== title ? titleEn : null,
                    work.prodYear,
                  ].filter((value) =>
                    value !== null && value !== undefined && value !== '').join(' · ');
                  return (
                    <CopyItem key={work.sourceWorkId} $unavailable={unavailable}>
                      {work.uploadFile?.originUrl && (
                        <ApprovalWorkThumbnail
                          originUrl={work.uploadFile.originUrl}
                          alt={title}
                        />
                      )}
                      <CopyInfo>
                        <CopyTitle>{title}</CopyTitle>
                        {meta && <CopyMeta>{meta}</CopyMeta>}
                      </CopyInfo>
                      {unavailable && (
                        <UnavailableLabel>
                          {getSettingsMessage('WORK_COPY_ITEM_UNAVAILABLE')}
                        </UnavailableLabel>
                      )}
                    </CopyItem>
                  );
                })}
              </CopyList>
            )}
            {hasUnavailableWork && (
              <RequestWarning role="alert">
                {getSettingsMessage('WORK_COPY_REQUEST_UNAVAILABLE')}
              </RequestWarning>
            )}
            {actionFailed && (
              <RequestError role="alert">
                {getSettingsMessage('APPROVAL_REQUEST_ACTION_FAILED')}
              </RequestError>
            )}
          </RequestContent>
          {isPending && (
            <RequestActions>
              {isIncoming ? (
                <>
                  <AcceptButton
                    $isMobile={isMobile}
                    disabled={areActionsDisabled || hasUnavailableCopyItem}
                    onClick={() => handleAccept(request)}
                  >
                    {getActionLabel(request.id, 'accept', 'ACCEPT')}
                  </AcceptButton>
                  <RejectButton
                    $isMobile={isMobile}
                    disabled={areActionsDisabled}
                    onClick={() => void handleAction('reject', request)}
                  >
                    {getActionLabel(request.id, 'reject', 'REJECT')}
                  </RejectButton>
                </>
              ) : (
                <RejectButton
                  $isMobile={isMobile}
                  disabled={areActionsDisabled}
                  onClick={() => void handleAction('cancel', request)}
                >
                  {getActionLabel(request.id, 'cancel', 'CANCEL')}
                </RejectButton>
              )}
            </RequestActions>
          )}
          {isCanceled && (
            <RequestActions>
              <CanceledButton $isMobile={isMobile} disabled>
                {getBtnName('CANCELED')}
              </CanceledButton>
            </RequestActions>
          )}
          {!isPending && !isCanceled && (
            <RequestActions>
              <ConfirmButton
                $isMobile={isMobile}
                disabled={areActionsDisabled}
                onClick={() => void handleAction('acknowledge', request)}
              >
                {getActionLabel(request.id, 'acknowledge', 'OK')}
              </ConfirmButton>
            </RequestActions>
          )}
        </RequestItem>
      );
    });
  };

  return (
    <RequestList $isMobile={isMobile} aria-live="polite">
      {renderListContent()}
    </RequestList>
  );
};

const RequestList = styled(SearchResults)<{ $isMobile: boolean }>`
  ${({ $isMobile }) => $isMobile && `
    min-height: 0;
    max-height: none;
    flex: 1;
  `}
`;

const RequestItem = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  padding: 10px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  align-items: ${({ $isMobile }) => ($isMobile ? 'stretch' : 'flex-start')};
  justify-content: space-between;
  gap: 12px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
`;

const RequestContent = styled.div`
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 5px;
`;

const RequestMessage = styled.div`
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const RequestError = styled.div`
  color: ${({ theme }) => theme.colors.color_alert_red};
  ${({ theme }) => theme.typography.Body_04};
`;

const RequestWarning = styled(RequestError)`
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const CopyList = styled.ul`
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  padding-left: 0;
  list-style: none;
`;

const CopyItem = styled.li<{ $unavailable: boolean }>`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 0;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
  opacity: ${({ $unavailable }) => ($unavailable ? 0.55 : 1)};
`;

const CopyInfo = styled.div`
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
`;

const CopyTitle = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const CopyMeta = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const UnavailableLabel = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.color_alert_red};
  ${({ theme }) => theme.typography.Body_04};
`;

const WorkThumbnailFrame = styled.div`
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const RequestActions = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const actionButtonStyle = `
  padding: 6px 10px;

  &:disabled {
    opacity: 0.45;
    cursor: default;
    transform: none;
  }
`;

const mobileActionButtonStyle = `
  width: fit-content;
  padding: 8px 1rem;
`;

const AcceptButton = styled(BlackFitBtn)<{ $isMobile: boolean }>`
  ${actionButtonStyle}
  ${({ $isMobile }) => $isMobile && mobileActionButtonStyle}
`;

const RejectButton = styled(PaperBlurFitBtn)<{ $isMobile: boolean }>`
  ${actionButtonStyle}
  ${({ $isMobile }) => $isMobile && mobileActionButtonStyle}
`;

const RetryButton = styled(PaperBlurFitBtn)<{ $isMobile: boolean }>`
  ${actionButtonStyle}
  ${({ $isMobile }) => $isMobile && `
    ${mobileActionButtonStyle}
    margin: 20px 0px 15px 0px;
  `}
`;

const ConfirmButton = styled(PaperBlurFitBtn)<{ $isMobile: boolean }>`
  ${actionButtonStyle}
  ${({ $isMobile }) => $isMobile && mobileActionButtonStyle}
`;

const CanceledButton = styled(PaperBlurFitBtn)<{ $isMobile: boolean }>`
  ${actionButtonStyle}
  ${({ $isMobile }) => $isMobile && mobileActionButtonStyle}
`;

const ErrorMessage = styled(SearchMessage)`
  flex-direction: column;
  gap: 10px;
`;

export default ApprovalRequestSection;
